import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ProductCard } from "@/components/ProductCard";
import { BundleCard } from "@/components/BundleCard";
import { getProducts, getCategories, getBundles } from "@/lib/api";

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState(["All Products"]);
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [category, setCategory] = useState(searchParams.get("category") || "All Products");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [sort, setSort] = useState("");

  useEffect(() => { getCategories().then(setCategories).catch(() => {}); }, []);
  useEffect(() => {
    setCategory(searchParams.get("category") || "All Products");
    if (searchParams.get("search") !== null) setSearch(searchParams.get("search"));
  }, [searchParams]);
  useEffect(() => { getBundles().then(setBundles).catch(() => setBundles([])); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await getProducts({ category, search: search || undefined, sort: sort || undefined });
      setProducts(data);
    } catch {
      setError(true);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [category, search, sort]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const pickCategory = (c) => {
    setCategory(c);
    const p = new URLSearchParams(searchParams);
    if (c === "All Products") p.delete("category"); else p.set("category", c);
    setSearchParams(p, { replace: true });
  };

  const q = search.trim().toLowerCase();
  const visibleBundles = (category === "All Products" || category === "All")
    ? bundles.filter((b) => !q || b.name.toLowerCase().includes(q) || (b.tagline || "").toLowerCase().includes(q))
    : [];
  const isEmpty = products.length === 0 && visibleBundles.length === 0;

  return (
    <div className="pt-12 pb-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-500">Catalogue</span>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">Research Collection</h1>
          <p className="mt-3 text-slate-600">Our current range of high-quality research peptides, supplied for lawful UK laboratory research.</p>
        </div>

        {/* Controls */}
        <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {categories.filter((c) => c !== "Bundles").map((c) => (
              <button
                key={c}
                data-testid="category-filter-btn"
                onClick={() => pickCategory(c)}
                className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                  category === c ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700 hover:border-slate-400 hover:text-slate-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="catalogue-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="h-10 w-full pl-9 bg-white border-slate-200 sm:w-56"
              />
            </div>
            <Select value={sort} onValueChange={(v) => setSort(v === "default" ? "" : v)}>
              <SelectTrigger data-testid="sort-select" className="h-10 w-[150px] bg-white border-slate-200">
                <SlidersHorizontal className="mr-1 h-4 w-4 text-slate-600" />
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Featured</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="name">Name A–Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl bg-slate-100" />)}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-4 py-20 text-center">
              <p className="text-slate-600">We couldn't load the catalogue right now.</p>
              <Button variant="outline" className="border-slate-200" onClick={load} data-testid="catalogue-retry">Try again</Button>
            </div>
          ) : isEmpty ? (
            <p className="py-20 text-center text-slate-500">No products match your search.</p>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              {visibleBundles.map((b, i) => <BundleCard key={b.id} bundle={b} index={products.length + i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
